/**
 * Upload size limits, shared by the API routes that accept multipart bodies and
 * by the client forms that submit them.
 *
 * These MUST stay below nginx's `client_max_body_size` (see
 * `config/nginx/hubspot-storefront.conf`). nginx enforces its own limit before
 * the request reaches Next.js, so anything the app "allows" above the proxy
 * ceiling dies as an HTML 413 that no route handler can turn into a useful
 * message. That is exactly how every upload over 1MB failed silently from
 * go-live until 2026-08-04: `client_max_body_size` was never set, nginx used
 * its 1MB default, and both upload forms showed a generic "please try again"
 * (one customer retried their 2.8MB exemption certificate 18 times).
 *
 * Deliberately import-free: client components import this module, and the S3
 * helpers that share the constants pull in the AWS SDK.
 */

/** Max size of a single uploaded document. */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

/** Max combined size of every file in one request. */
export const MAX_REQUEST_UPLOAD_BYTES = 20 * 1024 * 1024;

/** Max documents accepted in one request. */
export const MAX_DOCUMENTS_PER_REQUEST = 10;

/** Byte count as MB for user-facing copy: `10485760` → `"10MB"`. */
export function formatMb(bytes: number): string {
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10}MB`;
}

/**
 * User-facing message for an upload rejection. Covers the typed errors every
 * document-upload route returns from `collectAndUploadDocuments`, plus nginx's
 * bodyless 413, so each form doesn't re-invent the wording.
 *
 * Returns null for a code this module doesn't own, leaving form-specific errors
 * (`customer_not_found`, …) to the caller's own switch.
 */
export function describeUploadError(code: string | undefined, status: number): string | null {
  // 413 comes from nginx, not the app — the body is HTML, so there is no error
  // code to switch on. Name the cause instead of falling through to a generic
  // message, which is what made an oversized upload look like a random failure.
  if (status === 413) {
    return `Those documents are too large to upload — the limit is ${formatMb(MAX_REQUEST_UPLOAD_BYTES)} per request.`;
  }
  switch (code) {
    case 'document_required':
      return 'Please attach at least one document.';
    case 'too_many_files':
      return `Too many files (max ${MAX_DOCUMENTS_PER_REQUEST}).`;
    case 'file_too_large':
      return `A file is too large (max ${formatMb(MAX_DOCUMENT_BYTES)}).`;
    case 'upload_too_large':
      return `Those documents total more than ${formatMb(MAX_REQUEST_UPLOAD_BYTES)} — attach fewer or smaller files.`;
    case 'unsupported_file_type':
      return 'Unsupported file type — use PDF or an image.';
    default:
      return null;
  }
}

/**
 * Client-side pre-flight for an attachment set. Returns a ready-to-show message
 * when the files can't be accepted, or null when they can.
 *
 * The server re-checks all of this — this exists so the user gets an accurate
 * message instantly instead of an ambiguous failure after a long upload.
 */
export function checkUploadSizes(files: File[]): string | null {
  if (files.length > MAX_DOCUMENTS_PER_REQUEST) {
    return `Too many files — attach at most ${MAX_DOCUMENTS_PER_REQUEST}.`;
  }

  const oversized = files.find((file) => file.size > MAX_DOCUMENT_BYTES);
  if (oversized) {
    return `"${oversized.name}" is ${formatMb(oversized.size)} — each file must be under ${formatMb(MAX_DOCUMENT_BYTES)}.`;
  }

  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (total > MAX_REQUEST_UPLOAD_BYTES) {
    return `Those files total ${formatMb(total)} — the limit is ${formatMb(MAX_REQUEST_UPLOAD_BYTES)} per submission.`;
  }

  return null;
}
